/**
 * financialMath — the single source of truth for P&L arithmetic in the browser.
 *
 * QOE-CALC-1. This is the TypeScript mirror of `core/financial_math.py`. The
 * formulas below and the ones in that module must stay identical; both are
 * pinned to the same hand-derived golden vectors:
 *   - Python: tests/test_financial_math.py
 *   - TS:     frontend/src/lib/financialMath.test.ts
 * If you change a formula here, change it there, or the parity tests fail.
 *
 * CANONICAL FORMULAS (operating expenses EXCLUDE depreciation & amortization)
 *   gross_profit = revenue - cogs
 *   net_income   = revenue - cogs - opex - dep - amort - interest - tax
 *   ebitda       = gross_profit - opex                          // canonical
 *                == net_income + dep + amort + interest + tax   // identity
 *
 * Sources: SEC Non-GAAP C&DI 103.02; Reg S-K Item 10(e).
 *
 * OWNER COMP: adjusted_ebitda EXCLUDES owner compensation; sde =
 * adjusted_ebitda + full owner_comp_total. Normalizing owner comp to market
 * is a deferred decision (separate brief + CPA) — do not implement it here.
 */

import {
  routeFlexibleItems,
  yearOf,
  type FlexibleLineItem,
} from './flexibleLineItems'

/** A P&L cell as the workspace holds it: a number, or '' while unfilled. */
export type Cell = number | ''

export const OWNER_COMP_PREFIXES = ['owner_comp', 'owner comp'] as const

/** Blank cells contribute zero; they never poison a total with NaN. */
const n = (v: Cell | null | undefined): number =>
  typeof v === 'number' && Number.isFinite(v) ? v : 0

const filled = (v: Cell | null | undefined): v is number =>
  typeof v === 'number' && Number.isFinite(v)

// ---------------------------------------------------------------------------
// Core P&L figures — one function per figure
// ---------------------------------------------------------------------------

/** gross_profit = revenue - cogs. '' when either input is unfilled. */
export function grossProfit(revenue: Cell, cogs: Cell): Cell {
  if (!filled(revenue) || !filled(cogs)) return ''
  return revenue - cogs
}

/**
 * net_income = revenue - cogs - opex - dep - amort - interest - tax
 *
 * QOE-CALC-1: the defect this replaces computed `gross_profit - opex`, which
 * overstated net income by (dep + amort + interest + tax) — and, because
 * EBITDA was derived from it by adding those same four back, overstated
 * EBITDA by that amount too.
 */
export function netIncome(
  revenue: Cell,
  cogs: Cell,
  opex: Cell,
  depreciation: Cell = 0,
  amortization: Cell = 0,
  interest: Cell = 0,
  tax: Cell = 0,
): Cell {
  if (!filled(revenue) || !filled(cogs) || !filled(opex)) return ''
  return (
    revenue - cogs - n(opex) - n(depreciation) - n(amortization) - n(interest) - n(tax)
  )
}

/** Canonical EBITDA = gross_profit - operating_expenses (opex excludes D&A). */
export function ebitda(grossProfitValue: Cell, opex: Cell): Cell {
  if (!filled(grossProfitValue) || !filled(opex)) return ''
  return grossProfitValue - opex
}

/** EBITDA via the add-back identity, for rows with net income but no opex. */
export function ebitdaFromNetIncome(
  netIncomeValue: Cell,
  depreciation: Cell = 0,
  amortization: Cell = 0,
  interest: Cell = 0,
  tax: Cell = 0,
): Cell {
  if (!filled(netIncomeValue)) return ''
  return netIncomeValue + n(depreciation) + n(amortization) + n(interest) + n(tax)
}

/** operating_income = gross_profit - opex - dep - amort (AFTER D&A, unlike EBITDA). */
export function operatingIncome(
  grossProfitValue: Cell,
  opex: Cell,
  depreciation: Cell = 0,
  amortization: Cell = 0,
): Cell {
  if (!filled(grossProfitValue) || !filled(opex)) return ''
  return grossProfitValue - opex - n(depreciation) - n(amortization)
}

// ---------------------------------------------------------------------------
// Invariant check — the two EBITDA derivations must agree
// ---------------------------------------------------------------------------

/** (gross_profit - opex) - (net_income + D + A + I + T). Zero when consistent. */
export function ebitdaIdentityDelta(
  revenue: Cell,
  cogs: Cell,
  opex: Cell,
  depreciation: Cell = 0,
  amortization: Cell = 0,
  interest: Cell = 0,
  tax: Cell = 0,
): number {
  const gp = grossProfit(revenue, cogs)
  const lhs = ebitda(gp, opex)
  const ni = netIncome(revenue, cogs, opex, depreciation, amortization, interest, tax)
  const rhs = ebitdaFromNetIncome(ni, depreciation, amortization, interest, tax)
  return n(lhs) - n(rhs)
}

// ---------------------------------------------------------------------------
// Adjustments -> Adjusted EBITDA / SDE
// ---------------------------------------------------------------------------

export interface AdjustmentLike {
  category?: string | null
  amount?: number | string | null
  is_pending?: boolean | null
}

/** True when a category label means owner compensation, in any stored shape. */
export function isOwnerComp(category: string | null | undefined): boolean {
  const text = String(category ?? '').trim().toLowerCase()
  return OWNER_COMP_PREFIXES.some((p) => text.startsWith(p))
}

/** Pending ("Still Needed") adjustments are excluded from every figure. */
export function confirmedAdjustments<T extends AdjustmentLike>(adjustments: T[] | null | undefined): T[] {
  return (adjustments ?? []).filter((a) => !a.is_pending)
}

function sumAdjustments(
  adjustments: AdjustmentLike[] | null | undefined,
  ownerComp: boolean,
): number {
  return confirmedAdjustments(adjustments)
    .filter((a) => isOwnerComp(a.category) === ownerComp)
    .reduce((acc, a) => acc + (Number(a.amount) || 0), 0)
}

/** Sum of confirmed owner-compensation adjustments. SDE-only. */
export function ownerCompTotal(adjustments: AdjustmentLike[] | null | undefined): number {
  return sumAdjustments(adjustments, true)
}

/** adjusted_ebitda = ebitda + confirmed add-backs EXCLUDING owner comp. */
export function adjustedEbitda(
  ebitdaValue: Cell,
  adjustments: AdjustmentLike[] | null | undefined,
): Cell {
  if (!filled(ebitdaValue)) return ''
  return ebitdaValue + sumAdjustments(adjustments, false)
}

/** sde = adjusted_ebitda + FULL owner comp. */
export function sde(adjustedEbitdaValue: Cell, ownerCompTotalValue: number): Cell {
  if (!filled(adjustedEbitdaValue)) return ''
  return adjustedEbitdaValue + ownerCompTotalValue
}

/** House convention: TRUNCATED to 2dp, never rounded (2.67x, not 2.68x). */
export function sdeMultiple(purchasePrice: Cell, sdeValue: Cell): number | null {
  if (!filled(purchasePrice) || !filled(sdeValue) || sdeValue === 0) return null
  return Math.trunc((purchasePrice / sdeValue) * 100) / 100
}

export function adjEbitdaMargin(adjustedEbitdaValue: Cell, revenue: Cell): number | null {
  if (!filled(adjustedEbitdaValue) || !filled(revenue) || revenue === 0) return null
  return adjustedEbitdaValue / revenue
}

// ---------------------------------------------------------------------------
// Recompute-on-read — mirrors core.financial_math.derive_period
// ---------------------------------------------------------------------------

export interface RawPeriod {
  revenue?: Cell
  cogs?: Cell
  operating_expenses?: Cell
  depreciation?: Cell
  amortization?: Cell
  interest?: Cell
  tax?: Cell
  /** FIELDS-2: which fiscal year this row is, so flexible items can attach. */
  year?: number | string | null
}

export interface DerivedPeriod {
  gross_profit: Cell
  net_income: Cell
  ebitda: Cell
  adjusted_ebitda: Cell
  owner_comp_total: number
  sde: Cell
}

/**
 * Recompute every derived figure for one fiscal year from its RAW inputs.
 * Stored gross_profit / net_income / ebitda are ignored by design — derived
 * values are never trusted from storage (QOE-CALC-1 recompute-on-read).
 *
 * FIELDS-2: `flexibleItems` are the deal's custom line items, routed by their
 * `treatment` (see lib/flexibleLineItems). Only the items whose `year` matches
 * this row's `year` participate, so the same full list can be passed for every
 * period. Two deliberate constraints, mirroring derive_period in Python:
 *
 *   - if `raw` carries no usable `year`, NO flexible item is applied; applying
 *     every item to an unlabelled row would double count the moment a second
 *     period appeared;
 *   - the blank gates run against the REPORTED figures, so a flexible revenue
 *     item cannot conjure a period that has no revenue of its own.
 *
 * Items with no treatment, or one outside the controlled list, are counted
 * nowhere at all.
 */
export function derivePeriod(
  raw: RawPeriod,
  adjustments: AdjustmentLike[] | null | undefined = [],
  flexibleItems: FlexibleLineItem[] | null | undefined = [],
): DerivedPeriod {
  let revenue = raw.revenue ?? ''
  let opex = raw.operating_expenses ?? ''
  let effectiveAdjustments = adjustments

  if (flexibleItems && flexibleItems.length) {
    const periodYear = yearOf(raw.year)
    if (periodYear !== null) {
      const routed = routeFlexibleItems(flexibleItems, periodYear)
      if (routed.revenueDelta && filled(revenue)) revenue = revenue + routed.revenueDelta
      if (routed.adjustments.length) {
        effectiveAdjustments = [...(adjustments ?? []), ...routed.adjustments]
      }
      if (routed.opexDelta && filled(opex)) opex = opex + routed.opexDelta
    }
  }

  const gp = grossProfit(revenue, raw.cogs ?? '')
  const ni = netIncome(
    revenue,
    raw.cogs ?? '',
    opex,
    raw.depreciation ?? '',
    raw.amortization ?? '',
    raw.interest ?? '',
    raw.tax ?? '',
  )
  const eb = ebitda(gp, opex)
  const adjEb = adjustedEbitda(eb, effectiveAdjustments)
  const oc = ownerCompTotal(effectiveAdjustments)
  return {
    gross_profit: gp,
    net_income: ni,
    ebitda: eb,
    adjusted_ebitda: adjEb,
    owner_comp_total: oc,
    sde: sde(adjEb, oc),
  }
}
